import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import ExerciseFlowSteps from "../components/ExerciseFlowSteps";
import ExerciseWizard from "../components/ExerciseWizard";
import SeoHead from "../components/SeoHead";
import { getAllExercises, getExercisesByMuscleGroup, getMuscleGroupById } from "../services/exerciseService";
import { WIZARD_SKIPPED } from "../data/exerciseWizardConfig";
import { getWizardState } from "../services/exerciseWizardService";
import { muscleGroups } from "../data/muscleGroups";
import { getCustomExercises, getExerciseLog } from "../utils/localStorageUtils";
import { todayYMDLocal } from "../utils/dateUtils";
import { formatPromptDateLabel } from "../utils/formatDateLabels";
import "./SelectExercise.css";
import "../styles/Common.css";
import { useI18n } from "../i18n/i18n";
import { getLocalizedExerciseName } from "../utils/exerciseName";
import { buildLocalizedAlternates } from "../seo/routePolicy";
import { useSnapshotRenderMode } from "../utils/prerender";
import { trackEvent } from "../utils/analytics";
import { muscleGroupContent } from "../data/muscleGroupContent";

const SEARCH_RESULTS_LIMIT = 30;

export default function SelectExercise() {
    const { t, language, locale, localizePath } = useI18n();
    const { muscleGroup } = useParams();
    const location = useLocation();
    const todayDate = todayYMDLocal();
    const searchParams = useMemo(() => new URLSearchParams(location?.search || ""), [location?.search]);
    const targetDateParam = searchParams.get("date") || "";
    const isCustomDate = Boolean(targetDateParam && targetDateParam !== todayDate && /^\d{4}-\d{2}-\d{2}$/.test(targetDateParam));
    const normalizedRouteMuscleGroup = String(muscleGroup || "").toLowerCase();
    const snapshotMode = useSnapshotRenderMode();
    const [isMounted, setIsMounted] = useState(false);
    const [selectionMode, setSelectionMode] = useState("wizard");
    const [searchTerm, setSearchTerm] = useState("");
    const [equipmentFilter, setEquipmentFilter] = useState("All");
    const [muscleFilter, setMuscleFilter] = useState("All");
    const [optionsExpanded, setOptionsExpanded] = useState(false);
    const [wizardAnswers, setWizardAnswers] = useState(() =>
        normalizedRouteMuscleGroup ? { muscleGroup: normalizedRouteMuscleGroup } : {}
    );
    const [wizardHistory, setWizardHistory] = useState(() =>
        normalizedRouteMuscleGroup ? ["muscleGroup"] : []
    );
    const searchInputRef = useRef(null);
    const optionsCollapseTimeoutRef = useRef(null);
    const previousRouteMuscleGroupRef = useRef(normalizedRouteMuscleGroup);

    const scheduleOptionsCollapse = () => {
        if (optionsCollapseTimeoutRef.current) {
            clearTimeout(optionsCollapseTimeoutRef.current);
        }

        optionsCollapseTimeoutRef.current = setTimeout(() => {
            setOptionsExpanded(false);
            optionsCollapseTimeoutRef.current = null;
        }, 400);
    };

    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    const hasSearchTerm = normalizedSearchTerm.length > 0;

    // Match multi-token partials in any order, case-insensitive (e.g., "cu ez ar" -> "EZ Bar Curl")
    const normalizeForSearch = (str) =>
        str
            .toLowerCase()
            .replace(/[^\p{L}\p{N}]+/gu, " ")
            .replace(/\s+/g, " ")
            .trim();

    const nameMatchesSearch = (name, term) => {
        const tokens = normalizeForSearch(term).split(" ").filter(Boolean);
        if (tokens.length === 0) return false;
        const haystack = normalizeForSearch(name);
        return tokens.every((t) => haystack.includes(t));
    };

    const handleSearchChange = (value) => {
        const trimmedValue = value.trim();

        setSearchTerm(value);

        if (trimmedValue.length > 0) {
            scheduleOptionsCollapse();
        } else {
            if (optionsCollapseTimeoutRef.current) {
                clearTimeout(optionsCollapseTimeoutRef.current);
                optionsCollapseTimeoutRef.current = null;
            }

            setOptionsExpanded(true);
        }
    };

    const toggleOptions = () => {
        setOptionsExpanded((prev) => !prev);
    };

    const handleEquipmentFilterChange = (value) => {
        setEquipmentFilter(value);
        setOptionsExpanded(true);

        scheduleOptionsCollapse();
    };

    const handleMuscleFilterChange = (value) => {
        setMuscleFilter(value);
        setOptionsExpanded(true);

        scheduleOptionsCollapse();
    };

    const handleSearchFocus = () => {
        if (typeof window === "undefined") {
            return;
        }

        if (window.innerWidth <= 768 && searchInputRef.current) {
            const { top } = searchInputRef.current.getBoundingClientRect();
            const scrollTop = top + window.scrollY;
            window.scrollTo({ top: Math.max(scrollTop - 16, 0), behavior: "smooth" });
        }
    };

    const equipmentLabel = t(`equipment.${equipmentFilter}`) || equipmentFilter;
    const equipmentHeading =
        equipmentFilter === "All"
            ? t("select.allExercises")
            : t("select.equipmentExercises", { equipment: equipmentLabel });

    const equipmentOptions = [
        "All",
        "Barbell",
        "Dumbbell",
        "Cable",
        "Machine",
        "Smith Machine",
        "Specialty Bar",
        "Functional",
        "Kettlebell",
        "Medicine Ball",
        "Bodyweight",
        "Cardio Machine",
        "Other"
    ];

    const muscleOptions = [
        { id: "All", name: t("select.all") },
        ...muscleGroups.map((mg) => ({
            id: mg.id,
            name: t(`muscleGroups.${mg.id}`) || mg.name,
        }))
    ];
    const selectedMuscleName = muscleGroup
        ? ((t(`muscleGroupsGenitive.${muscleGroup}`) !== `muscleGroupsGenitive.${muscleGroup}` && t(`muscleGroupsGenitive.${muscleGroup}`)) || t(`muscleGroups.${muscleGroup}`) || getMuscleGroupById(muscleGroup)?.name || muscleGroup)
        : "";
    const toTitleCase = (value) =>
        String(value || "")
            .replace(/[_-]+/g, " ")
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
    const seoMuscleName = muscleGroup
        ? ((t(`muscleGroupsSearch.${muscleGroup}`) !== `muscleGroupsSearch.${muscleGroup}` && t(`muscleGroupsSearch.${muscleGroup}`)) || t(`muscleGroups.${muscleGroup}`) || toTitleCase(getMuscleGroupById(muscleGroup)?.name || muscleGroup))
        : "";
    const indexableBasePath = muscleGroup ? `/SelectExercise/${muscleGroup}` : "/SelectExercise";
    const activeLang = language === "uk" || String(locale).startsWith("uk") ? "uk" : "en";
    const groupContent = muscleGroup && muscleGroupContent[muscleGroup]
        ? (muscleGroupContent[muscleGroup][activeLang] || muscleGroupContent[muscleGroup].en)
        : null;
    const relatedGroupIds = muscleGroup && muscleGroupContent[muscleGroup]
        ? (muscleGroupContent[muscleGroup].related || [])
        : [];

    // Do not auto-reset filters or navigate away when searching.
    // Keep current filters/route and just change rendering below.
    // This avoids losing the preselected muscle group when typing in search.

    // Preselect muscle filter when arriving via a muscle group route
    useEffect(() => {
        if (muscleGroup) {
            setMuscleFilter(muscleGroup);
        }
    }, [muscleGroup]);

    useEffect(() => {
        if (previousRouteMuscleGroupRef.current === normalizedRouteMuscleGroup) {
            return;
        }

        previousRouteMuscleGroupRef.current = normalizedRouteMuscleGroup;
        setWizardAnswers(normalizedRouteMuscleGroup ? { muscleGroup: normalizedRouteMuscleGroup } : {});
        setWizardHistory(normalizedRouteMuscleGroup ? ["muscleGroup"] : []);
    }, [normalizedRouteMuscleGroup]);

    useEffect(() => {
        setIsMounted(true);

        return () => {
            if (optionsCollapseTimeoutRef.current) {
                clearTimeout(optionsCollapseTimeoutRef.current);
            }
        };
    }, []);

    const allowClientStorage = isMounted && !snapshotMode;

    const mergeExercisesWithCustom = (baseExercises) => {
        const merged = new Map();

        const customExercises = allowClientStorage ? getCustomExercises() : [];

        [...(baseExercises || []), ...customExercises].forEach((exercise) => {
            if (exercise?.id) {
                merged.set(exercise.id, exercise);
            }
        });

        return Array.from(merged.values());
    };

    const createExerciseQueryParts = [];
    if (searchTerm.trim()) {
        createExerciseQueryParts.push(`name=${encodeURIComponent(searchTerm.trim())}`);
    }
    if (isCustomDate) {
        createExerciseQueryParts.push(`date=${encodeURIComponent(targetDateParam)}`);
    }
    const createExerciseQueryStr = createExerciseQueryParts.join("&");
    const createExercisePath = localizePath(
        `/CreateExercise${createExerciseQueryStr ? `?${createExerciseQueryStr}` : ""}`
    );

    let directExercises = [];
    let indirectExercises = [];
    let allExercises = mergeExercisesWithCustom(getAllExercises());

    if (muscleGroup) {
        // Lookup for potential future labels
        directExercises = mergeExercisesWithCustom(getExercisesByMuscleGroup(muscleGroup, "primary"));
        indirectExercises = mergeExercisesWithCustom(getExercisesByMuscleGroup(muscleGroup, "secondary"));
        // muscleGroupName was unused; keeping lookup for potential future labels
    }

    const lastUsedByExercise = useMemo(() => {
        if (!allowClientStorage) {
            return {};
        }

        try {
            const log = getExerciseLog();
            const usageMap = {};

            Object.keys(log || {}).forEach((date) => {
                const dayLog = log[date] || {};
                Object.keys(dayLog).forEach((exId) => {
                    const sets = dayLog[exId] || [];
                    if (sets.length > 0 && (!usageMap[exId] || date > usageMap[exId])) {
                        usageMap[exId] = date;
                    }
                });
            });

            return usageMap;
        } catch (_) {
            return {};
        }
    }, [allowClientStorage]);

    const sortExercisesByRecent = (exerciseList) => [...exerciseList].sort((a, b) => {
        const aDate = lastUsedByExercise[a.id] || "";
        const bDate = lastUsedByExercise[b.id] || "";

        if (aDate && bDate && aDate !== bDate) {
            return bDate.localeCompare(aDate);
        }

        if (aDate && !bDate) {
            return -1;
        }

        if (!aDate && bDate) {
            return 1;
        }

        return a.name.localeCompare(b.name);
    });

    const searchMatches = hasSearchTerm
        ? sortExercisesByRecent(
            allExercises.filter((exercise) => {
                const localizedName = getLocalizedExerciseName(exercise, t);
                return (
                    nameMatchesSearch(localizedName, normalizedSearchTerm) ||
                    nameMatchesSearch(exercise.name, normalizedSearchTerm)
                );
            })
        )
        : [];

    const equipmentCounts = hasSearchTerm
        ? searchMatches.reduce((acc, exercise) => {
            const key = exercise.equipment || "Other";
            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {})
        : null;

    const muscleCounts = hasSearchTerm
        ? searchMatches.reduce((acc, exercise) => {
            const groups = Array.isArray(exercise.muscleGroups) ? exercise.muscleGroups : [];
            groups.forEach((mg) => {
                const key = mg?.group || null;
                if (key) {
                    acc[key] = (acc[key] || 0) + 1;
                }
            });
            return acc;
        }, {})
        : null;

    const getOptionCount = (option) => {
        if (!hasSearchTerm) {
            return null;
        }

        if (option === "All") {
            return searchMatches.length;
        }

        return equipmentCounts && Object.prototype.hasOwnProperty.call(equipmentCounts, option)
            ? equipmentCounts[option]
            : 0;
    };

    const getMuscleOptionCount = (optionId) => {
        if (!hasSearchTerm) {
            return null;
        }

        if (optionId === "All") {
            return searchMatches.length;
        }

        return muscleCounts && Object.prototype.hasOwnProperty.call(muscleCounts, optionId)
            ? muscleCounts[optionId]
            : 0;
    };

    let filteredExercises = allExercises;
    let filteredDirectExercises = directExercises;
    let filteredIndirectExercises = indirectExercises;

    if (hasSearchTerm) {
        filteredExercises = searchMatches;
        filteredDirectExercises = [];
        filteredIndirectExercises = [];
    }

    const applyEquipmentFilter = (exercises) => {
        if (equipmentFilter === "All") return exercises;
        return exercises.filter((exercise) => exercise.equipment === equipmentFilter);
    };

    const applyMuscleFilter = (exercises) => {
        if (muscleFilter === "All") return exercises;
        return exercises.filter((exercise) =>
            Array.isArray(exercise.muscleGroups) &&
            exercise.muscleGroups.some((mg) => mg?.group === muscleFilter)
        );
    };

    filteredExercises = applyEquipmentFilter(filteredExercises);
    filteredDirectExercises = applyEquipmentFilter(filteredDirectExercises);
    filteredIndirectExercises = applyEquipmentFilter(filteredIndirectExercises);

    filteredExercises = applyMuscleFilter(filteredExercises);
    filteredDirectExercises = applyMuscleFilter(filteredDirectExercises);
    filteredIndirectExercises = applyMuscleFilter(filteredIndirectExercises);

    // Build recent exercises list based on actual logged history (by date)
    const recentExercises = useMemo(() => {
        const sortedIds = Object.keys(lastUsedByExercise).sort((a, b) =>
            String(lastUsedByExercise[b]).localeCompare(String(lastUsedByExercise[a]))
        );

        const exerciseMap = Object.fromEntries((allExercises || []).map((ex) => [ex.id, ex]));
        return sortedIds
            .map((id) => exerciseMap[id])
            .filter(Boolean);
    }, [allExercises, lastUsedByExercise]);

    const recentExercisesForSelectedMuscle = useMemo(() => {
        if (!muscleGroup) {
            return [];
        }

        return recentExercises.filter((exercise) =>
            Array.isArray(exercise?.muscleGroups) &&
            exercise.muscleGroups.some((mg) => String(mg?.group).toLowerCase() === normalizedRouteMuscleGroup)
        );
    }, [muscleGroup, normalizedRouteMuscleGroup, recentExercises]);

    const wizardState = useMemo(
        () =>
            getWizardState({
                exercises: allExercises,
                answers: wizardAnswers,
                lastUsedByExercise,
            }),
        [allExercises, lastUsedByExercise, wizardAnswers]
    );

    const handleWizardAnswer = (questionId, value) => {
        const nextAnswers = {
            ...wizardAnswers,
            [questionId]: value,
        };
        setWizardAnswers(nextAnswers);
        setWizardHistory((prev) => [...prev, questionId]);

        const nextState = getWizardState({
            exercises: allExercises,
            answers: nextAnswers,
            lastUsedByExercise,
        });

        if (nextState.isComplete && nextState.remainingCount > 50) {
            handleSelectionModeChange("search");
        }
    };

    const handleWizardSkip = (questionId) => {
        const nextAnswers = {
            ...wizardAnswers,
            [questionId]: WIZARD_SKIPPED,
        };
        setWizardAnswers(nextAnswers);
        setWizardHistory((prev) => [...prev, questionId]);

        const nextState = getWizardState({
            exercises: allExercises,
            answers: nextAnswers,
            lastUsedByExercise,
        });

        if (nextState.isComplete && nextState.remainingCount > 50) {
            handleSelectionModeChange("search");
        }
    };

    const handleWizardBack = () => {
        const lastQuestionId = wizardHistory[wizardHistory.length - 1];
        if (!lastQuestionId) {
            return;
        }

        setWizardHistory((prev) => prev.slice(0, -1));
        setWizardAnswers((prev) => {
            const next = { ...prev };
            delete next[lastQuestionId];
            return next;
        });
    };

    const handleWizardReset = () => {
        setWizardAnswers({});
        setWizardHistory([]);
    };

    const handleWizardRemoveAnswer = (questionId) => {
        const answerIndex = wizardHistory.indexOf(questionId);
        if (answerIndex === -1) {
            return;
        }

        const nextHistory = wizardHistory.slice(0, answerIndex);
        setWizardHistory(nextHistory);
        setWizardAnswers((prev) => {
            const nextAnswers = {};
            nextHistory.forEach((id) => {
                if (Object.prototype.hasOwnProperty.call(prev, id)) {
                    nextAnswers[id] = prev[id];
                }
            });
            return nextAnswers;
        });
    };

    const handleSelectionModeChange = (mode) => {
        setSelectionMode(mode);

        if (mode !== "search") {
            setOptionsExpanded(false);
        }
    };

    const buildFiltersSuffix = (opts = {}) => {
        const { omitEquipment = false } = opts;
        const labels = [];
        if (!omitEquipment && equipmentFilter !== "All") {
            labels.push(equipmentLabel);
        }
        if (muscleFilter !== "All") {
            const mg = getMuscleGroupById(muscleFilter);
            labels.push(t(`muscleGroups.${muscleFilter}`) || mg?.name || muscleFilter);
        }
        return labels.length > 0 ? ` (${labels.join(", ")})` : "";
    };

    const searchResultsTitle = `${equipmentHeading}${buildFiltersSuffix({
        omitEquipment:
            equipmentFilter !== "All" &&
            equipmentHeading.toLowerCase().includes(String(equipmentLabel).toLowerCase())
    })}`;
    const searchResultCount = filteredExercises.length;
    const hasTooManySearchResults = hasSearchTerm && searchResultCount > SEARCH_RESULTS_LIMIT;
    const searchButtonLabel = hasSearchTerm
        ? t("select.searchCount", { count: searchResultCount })
        : t("select.existingExercises");

    const searchedRecentExercises = hasSearchTerm
        ? filteredExercises.filter((exercise) => Boolean(lastUsedByExercise[exercise.id]))
        : [];

    const searchedOtherExercises = hasSearchTerm
        ? filteredExercises.filter((exercise) => !lastUsedByExercise[exercise.id])
        : [];

    const renderHighlightedName = (name) => {
        if (selectionMode !== "search" || !hasSearchTerm) return name;

        const tokens = normalizeForSearch(searchTerm).split(" ").filter(Boolean);
        if (tokens.length === 0) return name;

        const lowerName = name.toLowerCase();
        const mask = new Array(name.length).fill(false);

        tokens.forEach((token) => {
            if (!token) return;
            let startIndex = 0;
            while (true) {
                const idx = lowerName.indexOf(token, startIndex);
                if (idx === -1) break;
                for (let i = idx; i < Math.min(idx + token.length, name.length); i += 1) {
                    mask[i] = true;
                }
                startIndex = idx + Math.max(1, token.length);
            }
        });

        const parts = [];
        let i = 0;
        let key = 0;
        while (i < name.length) {
            if (mask[i]) {
                let j = i;
                while (j < name.length && mask[j]) j += 1;
                parts.push(<span key={`b-${key++}`} className="exerciseMatchHighlight">{name.slice(i, j)}</span>);
                i = j;
            } else {
                let j = i;
                while (j < name.length && !mask[j]) j += 1;
                parts.push(<span key={`t-${key++}`}>{name.slice(i, j)}</span>);
                i = j;
            }
        }

        return <>{parts}</>;
    };

    const handleExerciseSelect = (exercise, method) => {
        trackEvent("select_exercise", { exercise_id: exercise.id, method });
    };

    const renderExerciseList = (exercises, title, afterTitle = null) => (
        <div className="exercise-category">
            <h2>{title}</h2>
            {afterTitle}
            {exercises.length === 0 ? (
                <p>{t("select.noExercisesFound")}</p>
            ) : (
                <ul className="exerciseList">
                    {exercises.map((exercise) => (
                        <li key={exercise.id} className="exerciseItem">
                            <div className="exerciseItemRow">
                                <Link
                                    to={localizePath(
                                        snapshotMode
                                            ? `/exercises/${exercise.id}`
                                            : (isCustomDate
                                                ? `/LogExercise/${exercise.id}/${targetDateParam}`
                                                : `/LogExercise/${exercise.id}`)
                                    )}
                                    className="exerciseLink appTextLink"
                                    onClick={() => handleExerciseSelect(exercise, selectionMode)}
                                >
                                    {renderHighlightedName(getLocalizedExerciseName(exercise, t))}
                                </Link>
                                <Link
                                    to={localizePath(`/exercises/${exercise.id}`)}
                                    className="exerciseGuideLink"
                                    title={t("select.exerciseGuide")}
                                    aria-label={`${t("select.exerciseGuide")}: ${getLocalizedExerciseName(exercise, t)}`}
                                >
                                    ℹ
                                </Link>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );

    const renderRecentExercises = (exercises) => (
        <div className="exercise-category">
            <h2>{t("select.recentExercises")}</h2>
            {exercises.length === 0 ? (
                <p>{t("select.noRecentExercises")}</p>
            ) : (
                <ul className="exerciseList">
                    {exercises.map((exercise) => (
                        <li key={exercise.id} className="exerciseItem">
                            {snapshotMode ? (
                                <span className="exerciseLink appTextLink">
                                    {getLocalizedExerciseName(exercise, t)}
                                </span>
                            ) : (
                                <Link
                                    to={localizePath(
                                        isCustomDate
                                            ? `/LogExercise/${exercise.id}/${targetDateParam}`
                                            : `/LogExercise/${exercise.id}`
                                    )}
                                    className="exerciseLink appTextLink"
                                    onClick={() => handleExerciseSelect(exercise, "recent")}
                                >
                                    {getLocalizedExerciseName(exercise, t)}
                                </Link>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );

    const renderSearchStateNotice = (message, type = "info") => (
        <div className={`searchStateNotice searchStateNotice--${type}`} role="status">
            <div className="searchStateNoticeIcon" aria-hidden="true">
                {type === "warning" ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                )}
            </div>
            <div className="searchStateNoticeBody">
                <p className="searchStateNoticeText">{message}</p>
            </div>
        </div>
    );

    const selectJsonLd = useMemo(() => {
        const siteUrl = "https://fit-log.com";
        const dashboardUrl = `${siteUrl}${localizePath("/")}`;
        const selectAllUrl = `${siteUrl}${localizePath("/SelectExercise")}`;

        const breadcrumbList = {
            "@type": "BreadcrumbList",
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "name": t("breadcrumbs.dashboard"),
                    "item": dashboardUrl
                },
                {
                    "@type": "ListItem",
                    "position": 2,
                    "name": t("breadcrumbs.selectExercise"),
                    "item": selectAllUrl
                }
            ]
        };

        if (muscleGroup) {
            const currentMuscleUrl = `${siteUrl}${localizePath(`/SelectExercise/${muscleGroup}`)}`;
            breadcrumbList.itemListElement.push({
                "@type": "ListItem",
                "position": 3,
                "name": selectedMuscleName,
                "item": currentMuscleUrl
            });

            const itemList = {
                "@type": "ItemList",
                "name": t("select.seoTitle", { muscle: seoMuscleName }),
                "description": t("select.seoDescriptionMuscle", { muscle: seoMuscleName }),
                "numberOfItems": directExercises.length,
                "itemListElement": directExercises.map((ex, index) => ({
                    "@type": "ListItem",
                    "position": index + 1,
                    "name": getLocalizedExerciseName(ex, t)
                }))
            };

            return {
                "@context": "https://schema.org",
                "@graph": [breadcrumbList, itemList]
            };
        }

        const itemList = {
            "@type": "ItemList",
            "name": t("select.seoTitleAll"),
            "description": t("select.seoDescription"),
            "numberOfItems": muscleGroups.length,
            "itemListElement": muscleGroups.map((mg, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "name": t(`muscleGroups.${mg.id}`) || mg.name,
                "url": `${siteUrl}${localizePath(`/SelectExercise/${mg.id}`)}`
            }))
        };

        return {
            "@context": "https://schema.org",
            "@graph": [breadcrumbList, itemList]
        };
    }, [muscleGroup, t, localizePath, selectedMuscleName, seoMuscleName, directExercises]);

    return (
        <div className="selectExercisePage pageContainer">
            <SeoHead
                title={
                    muscleGroup
                        ? t("select.seoTitle", { muscle: seoMuscleName })
                        : t("select.seoTitleAll")
                }
                description={
                    muscleGroup
                        ? t("select.seoDescriptionMuscle", { muscle: seoMuscleName })
                        : t("select.seoDescription")
                }
                canonicalPath={localizePath(indexableBasePath)}
                alternateLocales={buildLocalizedAlternates(indexableBasePath)}
                jsonLd={selectJsonLd}
            />
            <Breadcrumbs current={t("breadcrumbs.selectExercise")} />
            <Link to={localizePath("/")} className="flowBackLink appTextLink">
                <span className="flowBackLinkIcon" aria-hidden="true">←</span>
                <span className="flowBackLinkLabel">{t("common.back")}</span>
            </Link>
            <ExerciseFlowSteps activeStep={1} />
            <h1 className="sectionHeading">
                {muscleGroup
                    ? t("select.titleForMuscle", { muscle: selectedMuscleName })
                    : t("select.title")}
            </h1>

            {isCustomDate && (
                <div className="selectExerciseDateBanner" data-testid="select-target-date-banner">
                    <span className="selectExerciseDateBannerIcon" aria-hidden="true">📅</span>
                    <span>
                        {t("select.targetDateBanner", {
                            date: formatPromptDateLabel(targetDateParam, locale) || targetDateParam,
                        })}
                    </span>
                </div>
            )}

            <div className="selectionModeToggle" role="tablist" aria-label={t("select.modeSwitchAria")}>
                <button
                    type="button"
                    role="tab"
                    id="tab-search"
                    aria-controls="panel-search"
                    aria-selected={selectionMode === "search"}
                    className={`selectionModeButton ${selectionMode === "search" ? "isActive" : ""}`}
                    onClick={() => handleSelectionModeChange("search")}
                    title={t("select.modeSearch")}
                >
                    <svg
                        className="selectionModeIcon"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <span className="selectionModeLabel">{t("select.modeSearch")}</span>
                </button>
                <button
                    type="button"
                    role="tab"
                    id="tab-wizard"
                    aria-controls="panel-wizard"
                    aria-selected={selectionMode === "wizard"}
                    className={`selectionModeButton ${selectionMode === "wizard" ? "isActive" : ""}`}
                    onClick={() => handleSelectionModeChange("wizard")}
                    title={t("select.modeWizard")}
                >
                    <svg
                        className="selectionModeIcon"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72Z" />
                        <path d="m14 7 3 3" />
                        <path d="M5 6v4" />
                        <path d="M19 14v4" />
                        <path d="M10 2v2" />
                    </svg>
                    <span className="selectionModeLabel">{t("select.modeWizard")}</span>
                </button>
                <button
                    type="button"
                    role="tab"
                    id="tab-recent"
                    aria-controls="panel-recent"
                    aria-selected={selectionMode === "recent"}
                    className={`selectionModeButton ${selectionMode === "recent" ? "isActive" : ""}`}
                    onClick={() => handleSelectionModeChange("recent")}
                    title={t("select.modeRecent")}
                >
                    <svg
                        className="selectionModeIcon"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                    </svg>
                    <span className="selectionModeLabel">{t("select.modeRecent")}</span>
                </button>
            </div>

            {selectionMode === "wizard" ? (
                <div id="panel-wizard" role="tabpanel" aria-labelledby="tab-wizard">
                    <ExerciseWizard
                        t={t}
                        locale={locale}
                        targetDate={isCustomDate ? targetDateParam : null}
                        todayDate={todayDate}
                        wizardState={wizardState}
                        wizardHistory={wizardHistory}
                        onAnswer={handleWizardAnswer}
                        onBack={handleWizardBack}
                        onRemoveAnswer={handleWizardRemoveAnswer}
                        onReset={handleWizardReset}
                        onSkip={handleWizardSkip}
                        onGoToSearch={() => handleSelectionModeChange("search")}
                        renderExerciseList={renderExerciseList}
                    />
                </div>
            ) : selectionMode === "recent" ? (
                <div id="panel-recent" role="tabpanel" aria-labelledby="tab-recent">
                    {renderRecentExercises(muscleGroup ? recentExercisesForSelectedMuscle : recentExercises)}
                </div>
            ) : (
                <div id="panel-search" role="tabpanel" aria-labelledby="tab-search">
                    <div className="searchControls">
                        <input
                            ref={searchInputRef}
                            type="text"
                            className="searchInput"
                            placeholder={t("select.searchPlaceholder")}
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            onFocus={handleSearchFocus}
                        />
                        <button
                            type="button"
                            className="appButton searchIndicatorButton"
                            onClick={() => searchInputRef.current?.focus()}
                        >
                            {searchButtonLabel}
                        </button>
                        {snapshotMode ? (
                            <span className="appButton searchCreateButton" aria-disabled="true">
                                {t("select.createNewExercise")}
                            </span>
                        ) : (
                            <Link to={createExercisePath} className="appButton searchCreateButton">
                                {t("select.createNewExercise")}
                            </Link>
                        )}
                        <button
                            type="button"
                            className={`searchOptionsToggle appButton ${optionsExpanded ? "isExpanded" : ""}`}
                            onClick={toggleOptions}
                            aria-expanded={optionsExpanded}
                            aria-controls="searchOptions"
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                aria-hidden="true"
                                focusable="false"
                            >
                                <path d="M3 5h18a1 1 0 0 1 .78 1.63L14 15.5V20a1 1 0 0 1-1.53.85l-3-2A1 1 0 0 1 9 18v-2.5L2.22 6.63A1 1 0 0 1 3 5z"/>
                            </svg>
                            {(() => {
                                const activeFilterCount = (equipmentFilter !== "All" ? 1 : 0) + (muscleFilter !== "All" ? 1 : 0);
                                return activeFilterCount > 0
                                    ? t("select.filtersCount", { count: activeFilterCount })
                                    : t("select.filters");
                            })()}
                        </button>
                    </div>

                    <div
                        id="searchOptions"
                        className={`filterContainer ${optionsExpanded ? "isExpanded" : "isCollapsed"}`}
                        aria-hidden={!optionsExpanded}
                    >
                        <div className="filterGroup">
                            <div className="filterTitle">{t("select.equipment")}</div>
                            <div className="equipmentOptions" aria-label={t("select.equipmentFilterAria")}>
                            {equipmentOptions.map((option) => {
                                const optionCount = hasSearchTerm ? getOptionCount(option) : null;
                                const optionLabel = t(`equipment.${option}`) || option;

                                return (
                                    <label key={option} className="equipmentOption">
                                        <input
                                            type="radio"
                                            name="equipmentFilter"
                                            value={option}
                                            checked={equipmentFilter === option}
                                            onChange={(e) => handleEquipmentFilterChange(e.target.value)}
                                        />
                                        <span className="equipmentOptionLabel">
                                            {optionLabel}
                                            {hasSearchTerm && (
                                                <span className="equipmentCount">{optionCount}</span>
                                            )}
                                        </span>
                                    </label>
                                );
                            })}
                            </div>
                        </div>
                        <div className="filterGroup">
                            <div className="filterTitle">{t("select.muscleGroup")}</div>
                            <div className="muscleOptions" aria-label={t("select.muscleFilterAria")}>
                            {muscleOptions.map((option) => {
                                const optionCount = hasSearchTerm ? getMuscleOptionCount(option.id) : null;

                                return (
                                    <label key={option.id} className="muscleOption">
                                        <input
                                            type="radio"
                                            name="muscleFilter"
                                            value={option.id}
                                            checked={muscleFilter === option.id}
                                            onChange={(e) => handleMuscleFilterChange(e.target.value)}
                                        />
                                        <span className="muscleOptionLabel">
                                            {option.name}
                                            {hasSearchTerm && (
                                                <span className="muscleCount">{optionCount}</span>
                                            )}
                                        </span>
                                    </label>
                                );
                            })}
                            </div>
                        </div>
                    </div>

                    {!hasSearchTerm ? (
                        renderSearchStateNotice(t("select.searchStartPrompt"), "info")
                    ) : hasTooManySearchResults ? (
                        renderSearchStateNotice(t("select.searchTooManyResults"), "warning")
                    ) : (
                        searchedRecentExercises.length > 0 ? (
                            <>
                                {renderExerciseList(searchedRecentExercises, t("select.recentExercises"))}
                                {searchedOtherExercises.length > 0 &&
                                    renderExerciseList(
                                        searchedOtherExercises,
                                        `${t("select.otherExercises")}${buildFiltersSuffix()}`
                                    )}
                            </>
                        ) : (
                            renderExerciseList(filteredExercises, searchResultsTitle)
                        )
                    )}
                </div>
            )}

            {snapshotMode && directExercises.length > 0 && (
                <div className="prerenderExerciseList" style={{ marginTop: "24px" }}>
                    {renderExerciseList(directExercises, t("select.titleForMuscle", { muscle: selectedMuscleName }))}
                </div>
            )}

            {muscleGroup && groupContent && (
                <section className="muscleGroupGuideSection" aria-labelledby="muscle-guide-heading">
                    <h2 id="muscle-guide-heading" className="muscleGroupGuideHeading">
                        {groupContent.title}
                    </h2>
                    <div className="muscleGroupGuideGrid">
                        <div className="muscleGroupGuideCard">
                            <h3>{activeLang === "uk" ? "Анатомія та роль" : "Anatomy & Role"}</h3>
                            <p>{groupContent.overview}</p>
                        </div>
                        <div className="muscleGroupGuideCard">
                            <h3>{activeLang === "uk" ? "Найкращі вправи" : "Best Exercises"}</h3>
                            <p>{groupContent.bestExercises}</p>
                        </div>
                        <div className="muscleGroupGuideCard">
                            <h3>{activeLang === "uk" ? "Підходи, повторення та об'єм" : "Sets, Reps & Volume"}</h3>
                            <p>{groupContent.recommendations}</p>
                        </div>
                    </div>

                    {relatedGroupIds.length > 0 && (
                        <div className="relatedMusclesSection">
                            <h3>{t("select.relatedMuscles")}</h3>
                            <div className="relatedMusclesTags">
                                {relatedGroupIds.map((relId) => {
                                    const relName = t(`muscleGroups.${relId}`) || relId;
                                    return (
                                        <Link
                                            key={relId}
                                            to={localizePath(`/SelectExercise/${relId}`)}
                                            className="relatedMuscleTag"
                                        >
                                            {relName}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}
